import { createSelector } from '@reduxjs/toolkit';

import { IStoreState } from 'state/rootReducer';
import { cepFormatter } from 'shared/utils/formatters';
import { RequestStatus } from 'shared/constants/State';
import { CFMUpdateSteps } from '../types';

export const requestStatusSelector = (state: IStoreState) =>
  state.cfmUpdate.main.status;
export const professionalSelector = (state: IStoreState) =>
  state.cfmUpdate.main.professional;
export const cboSelector = (state: IStoreState) => state.cbo.listCBO.objects;
export const clinicSelector = (state: IStoreState) =>
  state.cfmUpdate.main.clinic;
export const clinicBillingSelector = (state: IStoreState) =>
  state.cfmUpdate.main.clinicBilling;

export const professionalFormSelector = createSelector(
  professionalSelector,
  cboSelector,
  (professional, cboList) => {
    const cpf = professional?.profile?.cpf ?? '';
    const rqe = professional?.rqe ?? '';
    const council_name = professional?.council_name ?? '';
    const council_number = professional?.council_number ?? '';
    const council_region = professional?.council_region ?? '';

    const foundCbo = cboList.find((value) => professional?.cbo === value.cbo);
    const cbo = foundCbo?.term ?? '';

    const professional_cnpj = professional?.profile.professional_cnpj ?? '';

    return {
      cpf,
      rqe,
      cbo,
      council_name,
      council_number,
      council_region,
      professional_cnpj,
    };
  },
);

export const cboInputSelector = createSelector(cboSelector, (cboList) => [
  { active: false, term: '', id: 0, cbo: 0 },
  ...cboList,
]);

export const clinicFormSelector = createSelector(clinicSelector, (clinic) => ({
  zip_code: cepFormatter(clinic?.zip_code ?? ''),
  address: clinic?.address ?? '',
  number: clinic?.number?.toString() ?? '',
  complement: clinic?.complement ?? '',
  city: clinic?.city ?? '',
  state: clinic?.state ?? '',
  neighborhood: clinic?.neighborhood ?? '',
  clinic_cnpj: clinic?.clinic_cnpj ?? '',
  clinic_as_billing: clinic?.clinic_as_billing ?? true,
}));

export const stepSelector = (state: IStoreState) => state.cfmUpdate.main.step;
export const stepsSelector = (state: IStoreState) => state.cfmUpdate.main.steps;
export const isAlertVisibleSelector = (state: IStoreState) =>
  state.cfmUpdate.main.isAlertVisible;

export const requestStatusDerivedSelector = createSelector(
  requestStatusSelector,
  (status) => ({
    requestStatus: status,
    isPending: status === RequestStatus.Pending,
    hasError: status === RequestStatus.Error,
  }),
);

export const clinicBillingFormSelector = createSelector(
  clinicBillingSelector,
  (clinicBilling) => ({
    person_kind: clinicBilling?.person_kind ?? '',
    responsible: clinicBilling?.responsible ?? '',
    cpf_cnpj: clinicBilling?.cpf_cnpj ?? '',
    billing_address: clinicBilling?.billing_address ?? '',
    billing_number: clinicBilling?.billing_number?.toString() ?? '',
    billing_address2: clinicBilling?.billing_address2 ?? '',
    billing_neighborhood: clinicBilling?.billing_neighborhood ?? '',
    billing_city: clinicBilling?.billing_city ?? '',
    billing_state: clinicBilling?.billing_state ?? '',
    billing_zip: clinicBilling?.billing_zip ?? '',
    billing_country: clinicBilling?.billing_country ?? '',
    billing_birthdate: clinicBilling?.billing_birthdate ?? '',
  }),
);

export const isModalOpenSelector = createSelector(
  stepSelector,
  (step) => ![CFMUpdateSteps.none, CFMUpdateSteps.close].includes(step),
);
export const professionalUpdateDoneSelector = (state: IStoreState) =>
  state.cfmUpdate.main.professionalUpdateDone;
export const clinicUpdateDoneSelector = (state: IStoreState) =>
  state.cfmUpdate.main.clinicUpdateDone;
export const clinicBillingUpdateDoneSelector = (state: IStoreState) =>
  state.cfmUpdate.main.clinicBillingUpdateDone;

export const getUserInfo = ({ userInfo }: IStoreState) => ({
  physicianId: userInfo.userData.physicianId,
  isSubscriber: userInfo.userData.isSubscriber,
  isClinicAdmin: userInfo.userData.isClinicAdmin,
  isAdmin: userInfo.userData.isAdmin,
  clinicId: userInfo.clinicId,
  subscriptionId: userInfo.userData.subscriptionId,
  userKind: userInfo.userData.userKind,
});

export const getPossibleStepsSelector = (state: IStoreState) =>
  state.cfmUpdate.main.possibleSteps;
